import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faPlus, faTrash, faBookmark, faXmark } from '@fortawesome/free-solid-svg-icons';
import AppSelect, { AppSelectTreeNode } from '../util/AppSelect';

function buildCatTree(rows: CatRow[], excludeId: number | null, parentId: number | null = null): AppSelectTreeNode[] {
  return rows
    .filter(r => r.parentId === parentId && r.id !== excludeId)
    .map(r => ({ value: r.id, label: r.name, children: buildCatTree(rows, excludeId, r.id) }));
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CatRow {
  id: number;
  name: string;
  parentId: number | null;
  supervisorId: number | null;
  taskIds: number[];
  task: { id: number; name: string; active: boolean }[];
  status: string;
}

interface UserItem { id: number; first_name: string; last_name: string; }
interface TaskItem { id: number; name: string; active: boolean; }
interface Template { id: number; name: string; taskIds: number[]; }
interface FormState { name: string; supervisorId: number | null; parentId: number | null; taskIds: number[]; isActive: boolean; }

type Mode = 'empty' | 'edit' | 'add';

const emptyForm = (): FormState => ({ name: '', supervisorId: null, parentId: null, taskIds: [], isActive: true });
const TPL_KEY = 'catTaskTemplates';

// ─── Reusable field styles ────────────────────────────────────────────────────

const fieldLabel: React.CSSProperties = {
  fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase',
  color: 'var(--text-color-calendar-week)', display: 'block', marginBottom: 3,
};

// kept for reference — actual inputs use className="timesheet-edit-input w-full"
const fieldInput: React.CSSProperties = {
  height: 30, border: '1px solid var(--border-input)', borderRadius: 6,
  background: 'var(--bg-input)', color: 'var(--text-color)',
  fontSize: 12, padding: '0 9px', outline: 'none', fontFamily: 'inherit', width: '100%',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};

// ─── Tree node (must be outside CategoryManagementNew to preserve identity) ───

interface TreeNodeProps {
  node: CatRow;
  depth: number;
  rows: CatRow[];
  openIds: Set<number>;
  selectedId: number | null;
  onToggle: (id: number) => void;
  onSelect: (cat: CatRow) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, depth, rows, openIds, selectedId, onToggle, onSelect }) => {
  const children = rows.filter(r => r.parentId === node.id);
  const isOpen = openIds.has(node.id);
  const isSel = selectedId === node.id;
  const hasChildren = children.length > 0;
  const hasTasks = node.taskIds.length > 0;

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '5px 8px',
          paddingLeft: 8 + depth * 16,
          borderRadius: 6, margin: '1px 0', cursor: 'pointer',
          background: isSel ? 'var(--active-row-color)' : 'transparent',
          transition: 'background .1s',
        }}
        onClick={() => onSelect(node)}
        onDoubleClick={() => hasChildren && onToggle(node.id)}
        onMouseEnter={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,.03)'; }}
        onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <button
          type="button"
          title={isOpen ? 'Collapse' : 'Expand'}
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: hasChildren ? 'pointer' : 'default', display: 'flex', alignItems: 'center', flexShrink: 0, width: 14 }}
        >
          {hasChildren && (
            <FontAwesomeIcon
              icon={faChevronRight}
              style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}
            />
          )}
        </button>
        <div
          title={hasTasks ? `${node.taskIds.length} tasks` : 'No tasks'}
          style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginRight: 3, background: hasTasks ? '#2563eb' : 'transparent', border: hasTasks ? 'none' : '1.5px solid var(--text-color-calendar-week)' }}
        />
        <span style={{ fontSize: 12, fontWeight: depth === 0 ? 700 : 400, color: isSel ? '#2563eb' : (node.status === 'inactive' ? 'var(--text-color-calendar-week)' : 'var(--text-color)'), flex: 1, opacity: node.status === 'inactive' ? 0.6 : 1 }}>
          {node.name}
        </span>
        {hasChildren && (
          <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{children.length}</span>
        )}
      </div>
      {isOpen && children.map(child => (
        <TreeNode key={child.id} node={child} depth={depth + 1} rows={rows} openIds={openIds} selectedId={selectedId} onToggle={onToggle} onSelect={onSelect} />
      ))}
    </>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const CategoryManagementNew: React.FC<{ updateTree?: () => void }> = ({ updateTree }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows]         = useState<CatRow[]>([]);
  const [users, setUsers]       = useState<UserItem[]>([]);
  const [taskList, setTaskList] = useState<TaskItem[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading]   = useState(true);

  // tree
  const [openIds, setOpenIds]   = useState<Set<number>>(new Set());

  // right panel
  const [mode, setMode]         = useState<Mode>('empty');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm]         = useState<FormState>(emptyForm());
  const [addParentId, setAddParentId] = useState<number | null>(null);
  const [saving, setSaving]     = useState(false);

  // template input
  const [tplInput, setTplInput] = useState('');
  const [tplOpen, setTplOpen]   = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: catsData }, { data: usersData }, { data: tasksData }] = await Promise.all([
        axios.get('category'), axios.get('users'), axios.get('task'),
      ]);
      const usersArr = usersData as UserItem[];
      setRows(
        (catsData as any[]).map((c) => ({
          id: c.id, name: c.name,
          parentId: c.parentId ?? null, supervisorId: c.supervisorId ?? null,
          taskIds: (c.task || []).filter((t: any) => t.active).map((t: any) => t.id),
          task: c.task || [],
          status: c.status || 'active',
        })).sort((a: CatRow, b: CatRow) => a.name.localeCompare(b.name))
      );
      setUsers(usersArr);
      setTaskList((tasksData as TaskItem[]).filter((t) => t.active).sort((a, b) => a.name.localeCompare(b.name)));
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try { const s = localStorage.getItem(TPL_KEY); if (s) setTemplates(JSON.parse(s)); } catch {}
  }, []);

  const saveTpls = (t: Template[]) => { setTemplates(t); localStorage.setItem(TPL_KEY, JSON.stringify(t)); };

  // ── Tree helpers ──────────────────────────────────────────────────────────
  const roots = rows.filter((r) => !r.parentId);

  const toggleRoot = (id: number) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Panel helpers ─────────────────────────────────────────────────────────
  const selectCat = (cat: CatRow) => {
    setMode('edit');
    setSelectedId(cat.id);
    setForm({ name: cat.name, supervisorId: cat.supervisorId, parentId: cat.parentId, taskIds: cat.taskIds, isActive: cat.status !== 'inactive' });
    setTplOpen(false); setTplInput('');
  };

  const startAdd = (parentId: number | null) => {
    setMode('add');
    setSelectedId(null);
    setAddParentId(parentId);
    setForm({ ...emptyForm(), parentId });
    setTplOpen(false); setTplInput('');
    // auto-expand parent
    if (parentId !== null) setOpenIds((prev) => { const next = new Set(prev); next.add(parentId); return next; });
  };

  const clearPanel = () => { setMode('empty'); setSelectedId(null); setForm(emptyForm()); };

  const setField = (key: keyof FormState, val: any) => setForm((p) => ({ ...p, [key]: val }));

  useEffect(() => {
    if (mode === 'empty') return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      if (containerRef.current?.contains(target as Node)) return;
      if (target.closest?.('[data-appselect-portal]')) return;
      clearPanel();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mode]);

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    if (!form.name.trim()) { enqueueSnackbar('Enter category name', { variant: 'error' }); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && selectedId) {
        await axios.put(`category/${selectedId}`, { name: form.name.trim(), supervisorId: form.supervisorId ?? null, taskId: form.taskIds, parentId: form.parentId ?? null, status: form.isActive ? 'active' : 'inactive' });
        enqueueSnackbar('Category updated', { variant: 'success' });
        await load(); updateTree?.();
      } else {
        await axios.post('category/add', { name: form.name.trim(), description: '1', parentId: form.parentId ?? null, taskId: form.taskIds, supervisorId: form.supervisorId ?? null });
        enqueueSnackbar('Category added', { variant: 'success' });
        clearPanel(); await load(); updateTree?.();
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    const cat = rows.find((r) => r.id === selectedId);
    if (!window.confirm(`Delete "${cat?.name}"?`)) return;
    try {
      const { data } = await axios.delete(`category/${selectedId}`);
      if (data?.deactivated) {
        enqueueSnackbar('Cannot delete — category has time records. It has been deactivated.', { variant: 'warning' });
      } else {
        enqueueSnackbar('Category deleted', { variant: 'success' });
      }
      clearPanel(); await load(); updateTree?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Failed to delete category', { variant: 'error' });
    }
  };

  // ── Template helpers ──────────────────────────────────────────────────────
  const applyTemplate = (tpl: Template) => {
    const merged = Array.from(new Set([...form.taskIds, ...tpl.taskIds]));
    setField('taskIds', merged);
  };

  const saveTemplate = () => {
    if (!tplInput.trim()) return;
    saveTpls([...templates, { id: Date.now(), name: tplInput.trim(), taskIds: form.taskIds }]);
    enqueueSnackbar('Template saved', { variant: 'success' });
    setTplInput(''); setTplOpen(false);
  };


  // ── Render ────────────────────────────────────────────────────────────────


  return (
    <div
      ref={containerRef}
      className="rounded-[5px] overflow-hidden"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: 'var(--border-input)', background: 'var(--bg-form)', height: 'calc(100vh - 203px)' }}
    >

      {/* ── LEFT: Tree ────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', borderRight: 'var(--border-input)', overflow: 'hidden' }}>

        {/* Tree header */}
        <div style={{ padding: '9px 12px 8px', borderBottom: 'var(--border-input)', background: 'var(--bg-top)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
            Categories
          </span>
          <Tooltip title="Add root category" placement="left">
            <button
              type="button"
              title="Add root category"
              onClick={() => startAdd(null)}
              style={{ width: 22, height: 22, border: '1.5px dashed rgba(37,99,235,.4)', borderRadius: 6, background: 'rgba(37,99,235,.06)', color: '#2563eb', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 9 }} />
            </button>
          </Tooltip>
        </div>

        {/* Tree body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,.12) transparent' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>Loading…</div>
          ) : (
            <>
              {roots.map(root => (
                <TreeNode key={root.id} node={root} depth={0} rows={rows} openIds={openIds} selectedId={selectedId} onToggle={toggleRoot} onSelect={selectCat} />
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── RIGHT: Detail / Form ──────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Panel header — only in empty state */}
        {mode === 'empty' && (
          <div style={{ padding: '10px 14px 9px', borderBottom: 'var(--border-input)', background: 'var(--bg-top)', flexShrink: 0, minHeight: 40, display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>Select a category</span>
          </div>
        )}

        {/* Panel body */}
        {mode === 'empty' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-color-calendar-week)', fontSize: 12 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity=".4">
              <path d="M3 6h18M3 12h12M3 18h8" strokeLinecap="round"/>
            </svg>
            <span>← Click a category in the tree</span>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 12, scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,.12) transparent' }}>

              {/* Parent */}
              <div>
                <label style={fieldLabel}>Parent</label>
                <AppSelect
                  value={form.parentId}
                  onChange={(v) => setField('parentId', v ? Number(v) : null)}
                  placeholder="— Root —"
                  style={{ width: '100%' }}
                  nodes={buildCatTree(rows, selectedId)}
                />
              </div>

              {/* Name */}
              <div>
                <label style={fieldLabel}>Name</label>
                <input
                  type="text" autoFocus value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                  placeholder="Category name…"
                  className="timesheet-edit-input w-full"
                />
              </div>

              {/* Active toggle */}
              <div>
                <label style={fieldLabel}>Status</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <div
                    onClick={() => setField('isActive', !form.isActive)}
                    style={{ width: 32, height: 18, borderRadius: 9, background: form.isActive ? '#2563eb' : 'var(--text-color-calendar-week)', transition: 'background .15s', position: 'relative', flexShrink: 0, cursor: 'pointer' }}>
                    <div style={{ position: 'absolute', top: 2, left: form.isActive ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                  </div>
                  <span style={{ fontSize: 12, color: form.isActive ? 'var(--text-color)' : 'var(--text-color-calendar-week)' }}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>

              {/* Supervisor */}
              <div>
                <label style={fieldLabel}>Supervisor</label>
                <AppSelect
                  value={form.supervisorId}
                  onChange={(v) => setField('supervisorId', v ? Number(v) : null)}
                  placeholder="None"
                  searchable={users.length > 7}
                  style={{ width: '100%' }}
                  options={[...users]
                    .sort((a, b) => a.first_name.localeCompare(b.first_name))
                    .map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))}
                />
              </div>

              {/* ── Tasks section ────────────────────────────── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

                {/* Tasks header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ ...fieldLabel, marginBottom: 0 }}>
                    Tasks{form.taskIds.length > 0 && <span style={{ color: '#2563eb', fontWeight: 700, marginLeft: 4 }}>({form.taskIds.length})</span>}
                  </span>
                  {!tplOpen ? (
                    <button type="button" onClick={() => setTplOpen(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-color-calendar-week)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: 0 }}>
                      <FontAwesomeIcon icon={faBookmark} style={{ fontSize: 9 }} />
                      Save as template
                    </button>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="text" autoFocus value={tplInput}
                        onChange={(e) => setTplInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); if (e.key === 'Escape') { setTplOpen(false); setTplInput(''); } }}
                        placeholder="Template name…"
                        style={{ height: 22, fontSize: 11, padding: '0 6px', border: 'var(--border-input)', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--text-color)', outline: 'none', fontFamily: 'inherit', width: 110 }}
                      />
                      <button type="button" onClick={saveTemplate}
                        style={{ height: 22, padding: '0 6px', fontSize: 10, fontWeight: 700, border: 'none', borderRadius: 4, background: '#2563eb', color: '#fff', cursor: 'pointer' }}>✓</button>
                      <button type="button" onClick={() => { setTplOpen(false); setTplInput(''); }}
                        style={{ height: 22, width: 22, fontSize: 10, border: 'var(--border-input)', borderRadius: 4, background: 'none', color: 'var(--text-color-calendar-week)', cursor: 'pointer' }}>✕</button>
                    </div>
                  )}
                </div>

                {/* Task multi-select */}
                <AppSelect
                  multiple
                  values={form.taskIds}
                  onChangeMultiple={(vals) => setField('taskIds', vals.map(Number))}
                  searchable={taskList.length > 7}
                  placeholder="No tasks selected"
                  options={taskList.map((t) => ({ value: t.id, label: t.name }))}
                  style={{ width: '100%' }}
                />

                {/* Saved templates */}
                {templates.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <span style={{ ...fieldLabel, marginBottom: 0 }}>Templates <span style={{ color: 'var(--text-color-calendar-week)', fontWeight: 400 }}>— click to apply</span></span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {templates.map((tpl) => (
                        <div key={tpl.id}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 8px 0 6px', background: 'var(--bg-input)', border: 'var(--border-input)', borderRadius: 10, fontSize: 11, color: 'var(--text-color-calendar)', cursor: 'pointer', transition: 'background .12s, border-color .12s' }}
                          onClick={() => applyTemplate(tpl)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--acc-bg, #eff6ff)'; e.currentTarget.style.borderColor = 'rgba(37,99,235,.25)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-input)'; e.currentTarget.style.borderColor = ''; }}
                        >
                          <FontAwesomeIcon icon={faBookmark} style={{ fontSize: 9, color: '#2563eb', opacity: .7 }} />
                          <span style={{ fontWeight: 500 }}>{tpl.name}</span>
                          <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginLeft: 1 }}>{tpl.taskIds.length}</span>
                          <button type="button" title="Delete template"
                            style={{ marginLeft: 2, width: 14, height: 14, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-color-calendar-week)', fontSize: 9, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => { e.stopPropagation(); saveTpls(templates.filter((x) => x.id !== tpl.id)); }}>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Panel footer */}
            <div style={{ padding: '10px 14px', borderTop: 'var(--border-input)', background: 'var(--bg-top-fix)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              {mode === 'edit' && (
                <button type="button" onClick={handleDelete} disabled={saving}
                  style={{ height: 28, padding: '0 10px', border: 'var(--border-input)', borderRadius: 6, background: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? .5 : 1 }}>
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: 5, fontSize: 11 }} />
                  Delete
                </button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button type="button" onClick={clearPanel}
                  style={{ height: 28, padding: '0 12px', border: 'var(--border-input)', borderRadius: 6, background: 'none', color: 'var(--text-color-calendar)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                  style={{ height: 28, padding: '0 14px', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? .6 : 1 }}>
                  {saving ? '…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryManagementNew;
